import type { RawOwnerGoalInput } from "../autonomy/goalUnderstanding";

import type { CyanOperationalTakeoverService } from "./cyanOperationalTakeoverService";

import type { ExecutionProgramAugmentationService } from "./executionProgramAugmentationService";

import type { ExecutionProgramRepository } from "./executionProgramState";

import { createOperationalTakeoverStatus } from "./operationalTakeoverStatus";

import {
  createTakeoverExecutionView,
  createTakeoverPreparationView,
  type CyanTakeoverLiveViewModel,
} from "./operationalTakeoverViewModel";

import type { OperationalTakeoverPreparation } from "./operationalTakeoverTypes";

export interface CyanOperationalSession {
  readonly preparation: OperationalTakeoverPreparation;

  readonly programId?: string;

  readonly view: CyanTakeoverLiveViewModel;
}

export interface StartOperationalSessionInput {
  readonly ownerGoal: RawOwnerGoalInput;

  readonly objective: string;
}

export interface ActivateOperationalSessionInput {
  readonly session: CyanOperationalSession;

  readonly requestedBy: string;

  readonly conversationId?: string;

  readonly projectId?: string;

  readonly repository?: string;

  readonly branch?: string;

  readonly headSha?: string;
}

export class CyanOperationalSessionService {
  constructor(
    private readonly takeover: CyanOperationalTakeoverService,

    private readonly programs: ExecutionProgramRepository,

    private readonly augmentation: ExecutionProgramAugmentationService,
  ) {}

  async prepare(
    input: StartOperationalSessionInput,
  ): Promise<CyanOperationalSession> {
    const preparation = await this.takeover.prepare(input);

    return {
      preparation,

      view: createTakeoverPreparationView(preparation),
    };
  }

  async activate(
    input: ActivateOperationalSessionInput,
  ): Promise<CyanOperationalSession> {
    const activation = await this.takeover.activate({
      preparation: input.session.preparation,

      requestedBy: input.requestedBy,

      conversationId: input.conversationId,

      projectId: input.projectId,

      repository: input.repository,

      branch: input.branch,

      headSha: input.headSha,
    });

    const status = createOperationalTakeoverStatus(activation.program);

    return {
      preparation: input.session.preparation,

      programId: activation.program.id,

      view: createTakeoverExecutionView(input.session.preparation, status),
    };
  }

  async refresh(
    session: CyanOperationalSession,
  ): Promise<CyanOperationalSession> {
    if (!session.programId) {
      return session;
    }

    const program = await this.programs.get(session.programId);

    if (!program) {
      throw new Error(`Execution program ${session.programId} not found.`);
    }

    return {
      ...session,

      view: createTakeoverExecutionView(
        session.preparation,
        createOperationalTakeoverStatus(program),
      ),
    };
  }

  async append(
    session: CyanOperationalSession,

    workPackages: Parameters<ExecutionProgramAugmentationService["append"]>[1],
  ): Promise<CyanOperationalSession> {
    if (!session.programId) {
      throw new Error("Operational session has not been activated.");
    }

    await this.augmentation.append(session.programId, workPackages);

    return this.refresh(session);
  }
}
